<template>
  <Modal position="end" ref="modalRef" @after-close="emit('closed')">
    <template #header>
      <span class="title">{{ title }}</span>
    </template>
    <template #body>
      <View direction="column" gap="lg">
        <Text v-if="!withoutTourReason" variant="body-medium" color="tertiary">{{ description }}</Text>

        <!-- The card itself leads to the renter's profile, as in the leads list -->
        <RenterProfileLink :property-id="inquiry.property.id" :renter-id="inquiry.renter.id" :decoration="false">
          <LeadCard :property-inquiry="inquiry" show-property />
        </RenterProfileLink>

        <View v-if="withoutTourReason" direction="column" gap="xs" padding="md" class="without-tour-reason">
          <Text variant="label-small" color="secondary">Reason they gave</Text>
          <Text variant="body-medium">{{ withoutTourReason }}</Text>
        </View>

        <View v-if="isPropertyPending" justify="center" padding-block="md">
          <LoadingIndicator />
        </View>

        <Hint v-else-if="propertyError" icon="alert-triangle">
          <Text variant="body-medium">
            We couldn't load how this home takes applications. Close the panel and try again.
          </Text>
        </Hint>

        <template v-else>
          <!-- Letting them apply and asking for a tour first are the two answers to the same ask. The titles are
               spans: a label-variant Text renders a <label>, and a click on a label inside the option's own picks nothing -->
          <RadioButtonGroup v-if="withoutTourReason" direction="column">
            <RadioButton v-model="answer" name="wants-to-apply-answer" value="apply">
              <View direction="column" gap="xs">
                <Text variant="label-medium" as="span">Let them apply</Text>
                <Text variant="body-small" color="secondary">{{ applyAnswerDescription }}</Text>
              </View>
            </RadioButton>
            <RadioButton v-model="answer" name="wants-to-apply-answer" value="tour">
              <View direction="column" gap="xs">
                <Text variant="label-medium" as="span">Ask them to tour first</Text>
                <Text variant="body-small" color="secondary">
                  Emily tells {{ renterFirstName }} you'd like them to see the home first, and helps them book a time.
                </Text>
              </View>
            </RadioButton>
          </RadioButtonGroup>

          <Textarea
            v-if="takesApplicationsOutsideTallo && answer === 'apply'"
            v-model="form.fields.message"
            placeholder="e.g. Apply at rentcafe.com/123-main and have a photo ID and your last two pay stubs ready"
            rows="5"
          />
        </template>
      </View>
    </template>

    <template #footer>
      <!-- A phone has the sheet's close button and no room for a third: the answer must stay in view -->
      <Button variant="ghost" class="cancel" @click="close">Cancel</Button>
      <View gap="md">
        <Button variant="outline" :disabled="!canAct" @click="ignoreConfirmRef?.show()">Ignore</Button>
        <Button
          v-if="answer === 'tour'"
          :disabled="!canAct"
          :loading="askToTourFirst.isLoading.value"
          @click="tourFirst"
        >
          Ask to tour first
        </Button>
        <Button
          v-else-if="takesApplicationsOutsideTallo"
          :disabled="!canAct || !answer || form.validation.$invalid"
          :loading="form.submitting"
          @click="sendNote"
        >
          Send
        </Button>
        <Button v-else :disabled="!canAct || !answer" :loading="isFetchingAttestations" @click="startSendApplication">
          Send application
        </Button>
      </View>
    </template>
  </Modal>

  <!-- Wider than a one-line question; on a phone the dialog is a full-width sheet either way -->
  <ConfirmModal
    ref="ignoreConfirmRef"
    width="30rem"
    :title="`Ignore ${renterFirstName}'s request?`"
    :description="`This card will be hidden forever, even if ${renterFirstName} asks to apply again, and nothing is sent to ${renterFirstName}. ${renterFirstName} stays in the Applications list for ${inquiry.property.displayName}, under Wants to apply, for 30 days after their latest request.`"
    confirm-label="Ignore"
    :loading="ignoreWantsToApply.isLoading.value"
    @confirm="ignore"
  />

  <SendApplicationModal
    ref="sendApplicationModalRef"
    :property-inquiry="inquiry"
    :renter="inquiry.renter"
    :attestations="attestations"
    :load-bundles="false"
    @application-sent="isApplicationSent = true"
    @closed="isApplicationSent && close()"
  />

  <ProvideInvestorAddressModal ref="provideInvestorAddressModalRef" @address-saved="startSendApplication" />
</template>

<script setup lang="ts">
import { required } from '@vuelidate/validators';
import { computed, ref } from 'vue';

import {
  Button,
  ConfirmModal,
  Hint,
  LoadingIndicator,
  Modal,
  RadioButton,
  RadioButtonGroup,
  serverErrorCode,
  Text,
  Textarea,
  useAlertsStore,
  useForm,
  View,
} from '@tallo/design-system';
import {
  applicationsService,
  PropertyAttestationsDto,
  transUnionAddressRequiredErrorCode,
} from '@tallo/investor/applications';
import { PropertyInquiryDto, usePropertyDetailsQuery } from '@tallo/investor/property';
import {
  RenterProfileLink,
  useAnswerWantsToApply,
  useAskToTourFirst,
  useIgnoreWantsToApply,
} from '@tallo/investor/renter';
import { ApplicationProvider } from '@tallo/property';

import LeadCard from '../property/pages/leads/lead-card.vue';
import ProvideInvestorAddressModal from '../renter-profile/components/provide-investor-address-modal/provide-investor-address-modal.vue';
import SendApplicationModal from '../renter-profile/components/send-application-modal/send-application-modal.vue';

const props = defineProps<{ inquiry: PropertyInquiryDto }>();
const emit = defineEmits<{ closed: [] }>();

const alertsStore = useAlertsStore();
const ignoreWantsToApply = useIgnoreWantsToApply();
const answerWantsToApply = useAnswerWantsToApply();
const askToTourFirst = useAskToTourFirst();

const modalRef = ref<InstanceType<typeof Modal>>();
const ignoreConfirmRef = ref<InstanceType<typeof ConfirmModal>>();
const sendApplicationModalRef = ref<InstanceType<typeof SendApplicationModal>>();
const provideInvestorAddressModalRef = ref<InstanceType<typeof ProvideInvestorAddressModal>>();

const renterFirstName = computed(() => props.inquiry.renter.user.name.split(' ')[0]);
// Set only for a renter who asked to apply without touring: the owner takes the application anyway, or asks them to
// tour first
const withoutTourReason = computed(() => props.inquiry.applicationWithoutTourReason?.trim() || null);
// Any other ask has one answer, the application; an ask without a tour waits for the owner to pick one
const answer = ref<'apply' | 'tour' | null>(withoutTourReason.value ? null : 'apply');
const title = computed(() =>
  withoutTourReason.value
    ? `${renterFirstName.value} wants to apply without a tour`
    : `${renterFirstName.value} wants to apply`,
);

// The list the card came from carries the unit without its lease conditions, so the unit's own read says who takes
// the applications. A unit with none on file takes Tallo's, as the backend reads it.
const {
  data: property,
  isPending: isPropertyPending,
  error: propertyError,
} = usePropertyDetailsQuery(() => props.inquiry.property.id);
const takesApplicationsOutsideTallo = computed(
  () => property.value?.leaseConditions?.applicationProvider === ApplicationProvider.OTHER,
);
const canAct = computed(() => !!property.value);

const description = computed(() =>
  takesApplicationsOutsideTallo.value
    ? `This home takes applications outside Tallo. Tell ${renterFirstName.value} where to apply and what to send, and Emily passes it on as a note from you.`
    : `Send ${renterFirstName.value} a Tallo application to start screening, or ignore the request if you're not moving forward.`,
);
const applyAnswerDescription = computed(() =>
  takesApplicationsOutsideTallo.value
    ? 'Write where to apply and what to send, and Emily passes it on as a note from you.'
    : 'Send a Tallo application to start screening.',
);

const form = useForm<{ message: string }>({ message: '' }, { message: { required } });

const attestations = ref<PropertyAttestationsDto | null>(null);
const isFetchingAttestations = ref(false);
const isApplicationSent = ref(false);

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

// The same first step as the renter profile's Send application: the screening questions on file decide what the
// send modal asks, and a company not yet set up at TransUnion asks this person for an address first
async function startSendApplication() {
  isFetchingAttestations.value = true;

  try {
    attestations.value = await applicationsService.getPropertyAttestations(props.inquiry.property.id);
    sendApplicationModalRef.value?.modalRef?.show();
  } catch (error) {
    if (serverErrorCode(error) === transUnionAddressRequiredErrorCode) {
      provideInvestorAddressModalRef.value?.modalRef?.show();
    } else {
      alertsStore.catchError(error);
    }
  } finally {
    isFetchingAttestations.value = false;
  }
}

async function sendNote() {
  try {
    await form.submit(async () => {
      await answerWantsToApply.mutateAsync({ inquiry: props.inquiry, message: form.fields.message });
      close();
    });
  } catch {
    // The mutation's toast says why nothing went out; the words stay on screen to reword or retry
  }
}

async function tourFirst() {
  try {
    await askToTourFirst.mutateAsync(props.inquiry);
    close();
  } catch {
    // The mutation's toast says why nothing went out; the panel stays open to try again
  }
}

async function ignore() {
  try {
    await ignoreWantsToApply.mutateAsync(props.inquiry);
    ignoreConfirmRef.value?.close();
    close();
  } catch {
    // The mutation's toast already said why; the dialog stays open to try again
  }
}

defineExpose({ open, close });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// On a phone the title takes two lines: split them evenly rather than leave one word on the second
.title {
  display: block;
  text-wrap: balance;
}

.cancel {
  @include mobile {
    display: none;
  }
}

.without-tour-reason {
  border-radius: var(--t-border-radius-sm);
  background-color: var(--t-color-neutral-100);
}
</style>
